import { withWindow } from '@shared/utils/withWindow';
import { addError } from '@shared/utils/logger';
import { useEventCallback } from '@shared/utils/hooks/useEventCallback';

interface ParsedUrlInfo {
  readonly hostname: string;
  readonly isRelative: boolean;
}

/**
 * Hook for safely parsing URLs (relative or absolute) and extracting hostname.
 * Handles both relative paths (e.g., /eventHandle/hotel/...) and absolute URLs.
 *
 * @returns A function that parses a URL and returns hostname info, or undefined on error
 */
export const useSafeUrlParser = () => {
  return useEventCallback((url: string, errorContext: string): ParsedUrlInfo | undefined => {
    return withWindow<ParsedUrlInfo | undefined>(global => {
      try {
        // Attempt to parse URL with global.location.origin as base to handle relative URLs
        const parsedUrl = new URL(url, global.location.origin);

        // Check if URL is relative by comparing origins
        const isRelative = parsedUrl.origin === global.location.origin && !url.startsWith('http');

        return {
          hostname: parsedUrl.hostname,
          isRelative,
        };
      } catch (error) {
        addError(error, { context: errorContext, url });
        return undefined;
      }
    }, undefined);
  });
};
